import { ChevronDown, DollarSign, Download, Wrench } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { PriorityDot } from "./PriorityDot";
import { SummaryCard } from "./SummaryCard";
import type { RecommendedStepSection } from "./csvExports";

interface RecommendedStepsCardProps {
  sections: RecommendedStepSection[];
  totalReplacementCost: number;
  actionBtnCls: string;
  onExportCSV: () => void;
  onExportXLSX: () => void;
}

export function RecommendedStepsCard({
  sections,
  totalReplacementCost,
  actionBtnCls,
  onExportCSV,
  onExportXLSX,
}: RecommendedStepsCardProps) {
  return (
    <SummaryCard
      icon={Wrench}
      iconColor="bg-amber-500/20 text-amber-400"
      tone="warning"
      title="Recommended Steps"
      action={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={actionBtnCls} disabled={sections.length === 0}>
              <Download className="h-3 w-3" />
              Export
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExportCSV}>
              Export CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportXLSX}>
              Export XLSX
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <div className="flex min-h-[620px] flex-1 flex-col">
        {sections.length > 0 ? (
          <div className="space-y-5 overflow-y-auto pr-1">
          {sections.map((s) => (
            <div key={s.category} className="space-y-2">
              {/* Section header */}
              <div className="flex items-center gap-2">
                <PriorityDot priority={s.priority} />
                <span className="flex-1 truncate text-sm font-bold uppercase tracking-wide text-foreground">
                  {s.category}
                </span>
                <span className="shrink-0 text-sm font-bold text-foreground tabular-nums">
                  {s.totalCost > 0 ? `$${s.totalCost.toLocaleString()}` : "—"}
                </span>
              </div>
              {/* Line items (expanded by default) */}
              {s.lineItems.length > 0 && (
                <div className="space-y-1 pl-5">
                  {s.lineItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="flex-1 truncate text-sm text-muted-foreground">
                        {item.description}
                      </span>
                      {item.cost !== null && item.cost > 0 && (
                        <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
                          ${item.cost.toLocaleString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground/70">No recommended steps</p>
        )}

        {/* Total maintenance cost — pinned at the bottom of the card */}
        <div className="mt-auto pt-3 border-t border-border/30 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
            <DollarSign className="h-3.5 w-3.5 text-amber-400" />
            Maintenance Cost
          </span>
          <span className="text-sm font-semibold text-foreground tabular-nums">
            {totalReplacementCost > 0 ? `$${totalReplacementCost.toLocaleString()}` : "—"}
          </span>
        </div>
      </div>
    </SummaryCard>
  );
}
